import { IDashboardWidget } from '../../../types/IDashboardWidget';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { WeeklyBalanceWidgetConfiguration } from './WeeklyBalanceWidgetConfiguration';
import DashboardWidgetContent from '../../dashboard-widget/DashboardWidgetContent';
import { DashboardWidgetProvider } from '../../dashboard-widget/DashboardWidgetContextProvider';
import DashboardWidgetConfigurationDrawer from '../../dashboard-widget/DasshboardWidgetConfigurationDrawer';
import { balanceService } from '@/modules/balances/api/balances/balance.service';
import { ChartColorName, ReactECharts, useChartColors } from '@/components/chart/ReactEcharts';
import { EChartsOption } from 'echarts';
import DashboardWidgetTitle from '../../dashboard-widget/DashboardWidgetTitle';
import { WeeklyBalanceLocationDTO } from '@/modules/balances/api/balances/balance.contracts';
import WeeklyBalanceWidgetConfigurationForm from './WeeklyBalanceWidgetConfigurationForm';
import { isNil } from 'lodash-es';
import { useDashboardContext } from '@/modules/dashboards/contexts/DashboardContextProvider';
import { WeeklyBalanceWidgetCatalogItem } from './WeeklyBalanceWidgetCatalogItem';
import { useTranslation } from '@/lib';
import { useTimerEndListener } from '@/providers/timer-provider/useTimerEndListener';
import { getWeekNumber } from '@/lib/utils/utils';
import DashboardWidgetConfigurationButton from '../../dashboard-widget/DashboardWidgetConfigurationButton';
import { DisplayMode } from '@/types/display-mode.enum';

type WeeklyBalanceWidgetProps = IDashboardWidget<WeeklyBalanceWidgetConfiguration>;

const WeeklyBalanceWidget: FunctionComponent<WeeklyBalanceWidgetProps> = ({ configuration, id }) => {
  const [weeklyBalance, setWeeklyBalance] = useState<WeeklyBalanceLocationDTO | null>(null);
  const { updateWidgetConfiguration } = useDashboardContext();
  const { getColorByName, isDarkMode } = useChartColors();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  // Listens to the timer context
  useTimerEndListener(handleTimerEnd, setIsLoading);

  const WEEKLY_BALANCE_WIDGET_CATALOG_ITEM = new WeeklyBalanceWidgetCatalogItem(t);

  async function fetchWeeklyBalance() {
    // Migrate the configuration to the new configuration if needed
    if (isNil(configuration.periodInWeeks) || configuration.periodInWeeks === 0) {
      const defaultWeeks = WEEKLY_BALANCE_WIDGET_CATALOG_ITEM.configuration.periodInWeeks;
      updateWidgetConfiguration<WeeklyBalanceWidgetConfiguration>(id, {
        periodInWeeks: defaultWeeks,
      });
      return; // Return early, useEffect will trigger again with the new value
    }

    // Migrate existing widgets to use hierarchical mode by default
    if (isNil(configuration.displayMode)) {
      updateWidgetConfiguration<WeeklyBalanceWidgetConfiguration>(id, {
        displayMode: WEEKLY_BALANCE_WIDGET_CATALOG_ITEM.configuration.displayMode,
      });
    }

    if (configuration.locationId && configuration.assetTypeId) {
      const response = await balanceService.getWeeklyBalanceForLocation({
        locationId: configuration.locationId,
        periodInDays: configuration.periodInWeeks * 7, // Convert weeks to days
        assetTypeId: configuration.assetTypeId,
        includeHierarchical: (configuration.displayMode ?? DisplayMode.HIERARCHICAL) === DisplayMode.HIERARCHICAL,
      });

      if (response.isSuccess) {
        setWeeklyBalance(response.payload);
      }
    }
    setIsLoading(false);
  }

  function handleTimerEnd() {
    fetchWeeklyBalance();
  }

  useEffect(() => {
    fetchWeeklyBalance();
  }, [configuration.locationId, configuration.assetTypeId, configuration.periodInWeeks, configuration.displayMode]);

  const chartOption: EChartsOption = useMemo(() => {
    if (!weeklyBalance?.balances) return {};

    const categories = weeklyBalance.balances.map((item) => `Week ${getWeekNumber(item.week)}`);
    const balanceData = weeklyBalance.balances.map((item) => item.balance);
    const incomingData = weeklyBalance.balances.map((item) => item.incoming);
    const outgoingData = weeklyBalance.balances.map((item) => item.outgoing);

    // Get highest balance count
    let highestBalanceCount = 0;
    weeklyBalance.balances.forEach((balance) => {
      if (balance.balance > highestBalanceCount) {
        highestBalanceCount = balance.balance;
      }
    });

    // Calculate left margin based on highest balance count
    let leftMargin = 30;
    if (highestBalanceCount > 100) {
      leftMargin = leftMargin + 8;
    }
    if (highestBalanceCount > 1000) {
      leftMargin = leftMargin + 8;
    }
    if (highestBalanceCount > 10000) {
      leftMargin = leftMargin + 8;
    }

    let highestIncomingOrOutgoing = 0;
    weeklyBalance.balances.forEach((balance) => {
      if (balance.incoming > highestIncomingOrOutgoing) {
        highestIncomingOrOutgoing = balance.incoming;
      } else if (balance.outgoing > highestIncomingOrOutgoing) {
        highestIncomingOrOutgoing = balance.outgoing;
      }
    });

    let rightMargin = 30;
    if (highestIncomingOrOutgoing > 100) {
      rightMargin += 8;
    }
    if (highestIncomingOrOutgoing > 1000) {
      rightMargin += 8;
    }
    if (highestIncomingOrOutgoing > 10000) {
      rightMargin += 8;
    }

    return {
      grid: {
        left: leftMargin,
        top: 30,
        right: rightMargin,
        bottom: 50, // More space for bottom legend
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(150,150,150,0.1)',
          },
        },
      },
      legend: {
        data: [t('balance'), t('incoming'), t('outgoing')],
        bottom: 0,
        orient: 'horizontal',
      },
      xAxis: [
        {
          type: 'category',
          data: categories,
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: t('balance'),
          position: 'left',

          axisLabel: {
            color: getColorByName(ChartColorName.BLUE),
          },
        },
        {
          type: 'value',
          name: t('flow'),
          position: 'right',
        },
      ],
      series: [
        {
          name: t('balance'),
          type: 'line',
          yAxisIndex: 0,
          data: balanceData,
          smooth: true,
          lineStyle: {
            color: getColorByName(ChartColorName.BLUE),
          },
          itemStyle: {
            color: getColorByName(ChartColorName.BLUE),
          },
        },
        {
          name: t('incoming'),
          type: 'bar',
          yAxisIndex: 1,
          data: incomingData,
          itemStyle: {
            color: getColorByName(ChartColorName.GREEN),
          },
        },
        {
          name: t('outgoing'),
          type: 'bar',
          yAxisIndex: 1,
          data: outgoingData,
          itemStyle: {
            color: getColorByName(ChartColorName.RED),
          },
        },
      ],
    };
  }, [weeklyBalance, t, isDarkMode]);

  return (
    <DashboardWidgetProvider widgetId={id ?? 'unknown'} isLoading={isLoading} setIsLoading={setIsLoading}>
      <DashboardWidgetContent>
        <DashboardWidgetConfigurationButton />
        <div className="flex w-full items-center">
          <DashboardWidgetTitle>
            {configuration.title}
            {weeklyBalance && `: ${weeklyBalance.assetTypeName}`}
          </DashboardWidgetTitle>
        </div>

        <div className="h-full w-full flex-1">
          {weeklyBalance?.balances.length ? (
            <ReactECharts
              option={chartOption}
              settings={{
                notMerge: true,
              }}
            />
          ) : (
            <div className="flex justify-center items-center h-full pb-6">{t('no_data_available')}</div>
          )}
        </div>
      </DashboardWidgetContent>

      <DashboardWidgetConfigurationDrawer widgetName={configuration.title}>
        <WeeklyBalanceWidgetConfigurationForm configuration={configuration} id={id} />
      </DashboardWidgetConfigurationDrawer>
    </DashboardWidgetProvider>
  );
};

export default WeeklyBalanceWidget;
